import { contactsOptions } from '@doneverse/data/queries'
import type { FlagKey } from '@doneverse/shared'
import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Activity,
  BookOpen,
  ClipboardList,
  Gauge,
  Handshake,
  Headset,
  LayoutDashboard,
  MessageSquare,
  MessageSquarePlus,
  Settings,
  TriangleAlert,
  Users,
  LogOut,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import type { RailSection } from './founderNavConfig'
import { countSectionItems, resolveRailSection, sectionHomeRoute } from './founderNavConfig'
import styles from './IconRailLayout.module.css'
import { ImpersonationBanner } from './ImpersonationBanner'
import { LockedNavTarget } from './LockedNavTarget'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import doneverseIcon from '@/assets/brand/doneverse-profile-logo.png'
import iconCoachingCommunityRail from '@/assets/brand/icon-coaching-community-rail.svg'
import iconOnboardingRail from '@/assets/brand/icon-onboarding-rail.svg'
import iconRoadmapRail from '@/assets/brand/icon-roadmap-rail.png'
import iconTrainingToolsRail from '@/assets/brand/icon-training-tools-rail.svg'
import { OnboardingShellProvider } from '@/components/onboarding/OnboardingShellContext'
import { OnboardingTracker } from '@/components/onboarding/OnboardingTracker'
import { CountBadge } from '@/components/ui/count-badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/AuthContext'
import { MobileSidebarContext } from '@/contexts/MobileSidebarContext'
import { TourAnchorProvider, useTourAnchors } from '@/contexts/TourAnchorContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useIsMobile } from '@/hooks/use-mobile'
import { useFaviconBadge } from '@/hooks/useFaviconBadge'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOnboardingLock } from '@/hooks/useOnboardingLock'
import { isAllowedForRestrictedDoer, isRestrictedDoer } from '@/lib/doerAccess'
import { FEEDBACK_SURFACE_ROLES } from '@/lib/feedback-access'
import { MESSAGING_SURFACE_ROLES } from '@/lib/messaging-access'
import { isNavTargetLocked, ONBOARDING_LOCK_MESSAGE } from '@/lib/onboardingLock'
import { TOUR_ANCHORS, type TourAnchorId } from '@/lib/productTour'
import { matchesPathPrefix } from '@/lib/routes'

interface RailItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  image?: string
  roles?: string[]
  // Stable anchor id for the founder product tour (lib/productTour). Set only
  // on the icons the tour points at; the node itself is registered through
  // TourAnchorContext so the tour follows this icon in and out of the rail.
  tourId?: TourAnchorId
  // Show this icon only if at least one of these flags is enabled. Sections
  // whose children are all flag-gated set this; sections with an always-on
  // child (e.g. Onboarding → Business Brief) leave it unset.
  anyFlags?: FlagKey[]
  // Sectioned rail icons (founder + doer) own a section. On mobile, tapping a
  // non-active rail icon for a section with >1 visible items previews that
  // section's submenu in the drawer instead of navigating away.
  section?: RailSection
}

const railMainItems: RailItem[] = [
  {
    to: '/admin/stats',
    label: 'Platform',
    icon: Gauge,
    roles: ['admin'],
    section: 'admin-platform',
  },
  {
    to: '/admin/users',
    label: 'People',
    icon: Users,
    roles: ['admin'],
    section: 'admin-people',
  },
  {
    to: '/admin/playbooks',
    label: 'Content',
    icon: BookOpen,
    roles: ['admin'],
    section: 'admin-content',
  },
  {
    to: '/admin/coaching-sessions',
    label: 'Coaching',
    icon: Headset,
    roles: ['admin'],
    section: 'admin-coaching',
  },
  {
    // Section icons land on their section's first sidebar item (resolved per
    // role/flags via sectionHomeRoute), so `to` is only a fallback + stable key.
    to: '/doing-blueprint',
    label: 'Roadmap + Blueprint',
    icon: LayoutDashboard,
    image: iconRoadmapRail,
    roles: ['founder', 'doer'],
    section: 'roadmap',
    tourId: TOUR_ANCHORS.railRoadmap,
  },
  {
    to: '/business-brief',
    label: 'Onboarding',
    icon: LayoutDashboard,
    image: iconOnboardingRail,
    roles: ['founder'],
    section: 'onboarding',
  },
  {
    to: '/coaching-calls',
    label: 'Coaching + Community',
    icon: LayoutDashboard,
    image: iconCoachingCommunityRail,
    roles: ['founder'],
    section: 'coaching-community',
    anyFlags: ['coaching-call-recordings', 'groups'],
    tourId: TOUR_ANCHORS.railCoachingCommunity,
  },
  {
    to: '/masterclass-vault',
    label: 'Training + Tools',
    icon: LayoutDashboard,
    image: iconTrainingToolsRail,
    roles: ['founder'],
    section: 'training-tools',
    anyFlags: ['masterclass-vault', 'doers-university', 'tech-stack-finder', 'founders-lounge'],
    tourId: TOUR_ANCHORS.railTrainingTools,
  },
  {
    // Doer onboarding rail. Its section holds their founder's Business Brief,
    // the Launch Checklist, and their own profile (`/doer-profile`);
    // kick-off-call is founder-only.
    //
    // Labelled for the SECTION, as the founder's equivalent above is, rather
    // than for `to`: the icon lands on whichever item the doer can actually
    // reach (`railItemTo`), which for a doer with no founder yet is their own
    // profile. A label naming one destination would be wrong on the other.
    to: '/business-brief',
    label: 'Onboarding',
    icon: LayoutDashboard,
    image: iconOnboardingRail,
    roles: ['doer'],
    section: 'onboarding',
  },
  {
    to: '/teams',
    label: 'Doing Teams',
    icon: Handshake,
    roles: ['doer'],
    section: 'doing-teams',
  },
  {
    // Doer coaching rail: lands on the single AI-coaching route (pre-session prep
    // until a session is in progress, then the coaching chat). Coaching is all
    // the section holds for a doer since Community became founder-only (ClickUp
    // 86d3ve54a), so `pre-session-prep` alone decides whether the icon shows —
    // and while that flag is off, a doer has no icon here at all. The section id
    // stays `coaching-community`: it is shared with the founder rail, whose
    // section really does hold both.
    to: '/ai-coaching',
    label: 'Coaching',
    icon: LayoutDashboard,
    image: iconCoachingCommunityRail,
    roles: ['doer'],
    section: 'coaching-community',
    anyFlags: ['pre-session-prep'],
  },
  {
    to: '/projects',
    label: 'Dashboard',
    icon: LayoutDashboard,
    roles: ['doing_team'],
  },
  {
    to: '/intake',
    label: 'Intake Queue',
    icon: ClipboardList,
    roles: ['intake_team'],
  },
  {
    to: '/escalations',
    label: 'Escalation Console',
    icon: TriangleAlert,
    roles: ['escalation_officer'],
  },
  {
    to: '/admin/activity-log',
    label: 'Activity Log',
    icon: Activity,
    roles: ['dsl'],
  },
  {
    to: '/admin/activity-summary',
    label: 'Activity Summary',
    icon: Gauge,
    roles: ['dsl'],
  },
  {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquare,
    roles: MESSAGING_SURFACE_ROLES,
    tourId: TOUR_ANCHORS.railMessages,
  },
  {
    // Feedback is its own top-level section, alongside Messages — a pop-up
    // cannot give threaded replies the scroll space they need. Every role that
    // can reach the submit dialog (that is, every role, via TopBarActions)
    // needs somewhere to read the reply. Admins are the one exception: they
    // track their items in the queue at /admin/feedback instead.
    to: '/feedback',
    label: 'Feedback',
    icon: MessageSquarePlus,
    roles: FEEDBACK_SURFACE_ROLES,
    // Flagged off until launch. Only the nav entry hides: the routes, the
    // Feedback button and the admin queue keep working, so anyone who submits
    // still gets their reply and can still open the thread it points at.
    anyFlags: ['feedback-loop'],
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Settings,
    roles: ['founder', 'doer', 'doing_team', 'intake_team', 'admin', 'escalation_officer', 'dsl'],
  },
]

/**
 * The authenticated shell.
 *
 * Wrapped in the product tour's anchor registry rather than registering into a
 * provider further up: every node the tour points at — rail icons, sidebar
 * items, the mobile hamburger — is a descendant of this route's component, and
 * scoping the registry to them keeps it empty for every shell that has no tour.
 */
export function IconRailLayout() {
  return (
    <TourAnchorProvider>
      <IconRailShell />
    </TourAnchorProvider>
  )
}

function IconRailShell() {
  const { profile, logout } = useAuth()
  const { workspaces } = useWorkspace()
  const { hasFlag } = useFeatureFlags()
  const [mobileOpen, setMobileOpenState] = useState(false)
  const [previewSection, setPreviewSection] = useState<RailSection | null>(null)
  const isMobile = useIsMobile()
  const { pathname } = useLocation()
  // Founders, doers and admins all navigate a sectioned rail; the mobile submenu
  // preview applies to each. Other roles have flat, sub-section-less rails.
  const usesRailSections =
    profile?.role === 'founder' || profile?.role === 'doer' || profile?.role === 'admin'

  const setMobileOpen = useCallback((open: boolean) => {
    setMobileOpenState(open)
    if (!open) setPreviewSection(null)
  }, [])

  // A restricted doer (no founder, or the dashboard hidden for them) is limited
  // to a short allowlist of surfaces. Home lives in the roadmap section, so keep
  // that rail icon (its sidebar shows only Start Here) and hide every other
  // section icon. The allowlist itself comes from lib/doerAccess, which the
  // router enforces — read it here rather than naming routes twice, so the rail
  // and the guard can't drift.
  const restrictedDoer = isRestrictedDoer(profile, hasFlag)
  // No switcher for a restricted doer either: it lists their founders by name,
  // and switching workspace changes nothing on the pages they can reach.
  const showWorkspaceSwitcher = workspaces.length > 1 && !restrictedDoer

  // Founder onboarding: every section except the active step's stays visible
  // but unclickable until steps 1–4 are done. Reads the same lock the router's
  // beforeLoad guard reads, so a live icon is always one the guard lets through.
  const onboardingLock = useOnboardingLock()
  // Step 4's product tour spotlights this chrome. Registering hands it the live
  // nodes; an icon whose section is flagged off simply never registers, and the
  // tour falls back rather than pointing at nothing.
  const { register } = useTourAnchors()

  // Where the icon navigates: a section icon lands on that section's first
  // visible sidebar item (role/flag-aware); non-section icons use their own `to`.
  //
  // This, not the declared `to`, is also what the onboarding lock is tested
  // against — the roadmap icon lands on /start-here, which stays reachable while
  // the founder is locked, and the onboarding icon lands on /business-brief,
  // which is live exactly while step 2 is the active step.
  const railItemTo = (item: RailItem) =>
    (item.section && sectionHomeRoute(item.section, profile?.role, hasFlag, restrictedDoer)) || item.to

  const isVisible = (item: RailItem) => {
    if (item.roles && !item.roles.includes(profile?.role || '')) return false
    if (item.anyFlags && !item.anyFlags.some((flag) => hasFlag(flag))) return false
    // Tested against the destination the icon actually navigates to, not its
    // declared `to` — the same question `railItemTo` answers for the lock. A
    // section icon names its section's landing page, which is not necessarily
    // the item a restricted doer is allowed: the doer onboarding icon declares
    // /business-brief but lands a founder-less doer on /doer-profile, the one
    // page in that section they are meant to be filling in.
    if (restrictedDoer && !isAllowedForRestrictedDoer(railItemTo(item)) && item.section !== 'roadmap') {
      return false
    }
    return true
  }

  const messagesItem = railMainItems.find((item) => item.to === '/messages')
  const messagesVisible = messagesItem ? isVisible(messagesItem) : false
  const { data: contacts } = useQuery({ ...contactsOptions(), enabled: messagesVisible })
  const unreadMessageCount = useMemo(
    () => (contacts ?? []).reduce((sum, c) => sum + (c.unread_count || 0), 0),
    [contacts],
  )
  // Same count, same red dot, on the browser tab — the rail badge is invisible
  // to a user sitting in another tab. Driven from here so the two can't
  // disagree: no unread badge on the rail, no dot on the favicon.
  useFaviconBadge(unreadMessageCount > 0)

  const matchesRailRoute = (item: RailItem) => matchesPathPrefix(pathname, item.to)

  const visibleMain = railMainItems.filter(isVisible)
  // null → no section owns this path (e.g. /messages); only a direct-route
  // rail item (matchesRailRoute) can be active then.
  const activeRailSection = usesRailSections ? resolveRailSection(pathname, profile?.role) : null

  const isItemActive = (item: RailItem) => {
    if (item.section) {
      return usesRailSections && item.section === (previewSection ?? activeRailSection)
    }
    return matchesRailRoute(item)
  }

  const iconClass = (item: RailItem) => {
    return [styles.iconButton, isItemActive(item) ? styles.active : ''].filter(Boolean).join(' ')
  }

  // On mobile, tapping a rail icon that owns a section with >1 visible items
  // should reveal that section's submenu in the drawer instead of navigating.
  const handleRailClick = (e: React.MouseEvent, item: RailItem) => {
    if (!isMobile || !usesRailSections || !item.section) {
      setMobileOpen(false)
      return
    }
    const currentSection = previewSection ?? resolveRailSection(pathname, profile?.role)
    if (item.section === currentSection) {
      setMobileOpen(false)
      return
    }
    if (countSectionItems(item.section, profile?.role, hasFlag, restrictedDoer) > 1) {
      e.preventDefault()
      setPreviewSection(item.section)
      setMobileOpenState(true)
      return
    }
    setMobileOpen(false)
  }

  const sidebarCtx = useMemo(
    () => ({ mobileOpen, setMobileOpen, previewSection, setPreviewSection }),
    [mobileOpen, setMobileOpen, previewSection],
  )

  // The avatar is a link to /settings, so the lock reaches it like any other
  // nav target — but its tooltip normally carries the user's name and email,
  // which is why it does not go through the rail item's branch above.
  const avatarLocked = isNavTargetLocked('/settings', onboardingLock)
  const avatarInner = profile?.profile_picture_url ? (
    <img src={profile.profile_picture_url} alt={profile.name || 'Profile'} className={styles.avatarImage} />
  ) : (
    <span data-testid="sidebar-user-initial">
      {profile?.name?.charAt(0) || profile?.email?.charAt(0) || '?'}
    </span>
  )

  return (
    <MobileSidebarContext.Provider value={sidebarCtx}>
      {/* Founder onboarding. The provider wraps the whole shell because the
          panel that drives it is rendered per page by PageLayout, deep inside
          the Outlet below, while the surfaces it opens are mounted here — see
          OnboardingShellContext. */}
      <OnboardingShellProvider>
        <div className={styles.outerWrapper} data-testid="app-shell">
          <ImpersonationBanner />
          {/* Onboarding's shell half: the full-screen step surfaces, the unlock
            and finish moments, and the minimized pill. Mounted here — the
            outermost authenticated shell — so it survives the navigations the
            in-page panel does not. Renders null for everyone else and whenever
            the founder-onboarding flag is off, so it costs no layout. */}
          <OnboardingTracker />
          <div className={styles.wrapper}>
            {mobileOpen && <div className={styles.overlay} onClick={() => setMobileOpen(false)} />}

            {/* The product tour's last-resort anchor on desktop: whatever else is
              flagged off, the rail is always mounted. Below 1024px it is
              translated off-canvas, which the tour detects as "not on screen"
              and falls through to the mobile hamburger instead. */}
            <aside ref={register(TOUR_ANCHORS.rail)} className={styles.rail} data-open={mobileOpen}>
              <div className={styles.railInner}>
                {/* Scrolls on its own when the window is too short to hold every
                    icon, so the avatar + Logout below stay pinned and reachable
                    — see .topSection in the stylesheet. */}
                <div className={styles.topSection} data-testid="rail-scroll">
                  <img src={doneverseIcon} alt="Doneverse" className={styles.logo} />
                  {showWorkspaceSwitcher && <WorkspaceSwitcher />}
                  <nav className={styles.navGroup}>
                    {visibleMain.map((item) => {
                      // Drives aria-current below. TanStack Link also injects its own
                      // aria-current="page" whenever the link's `to` matches the URL,
                      // so two icons can briefly carry it during mobile section preview.
                      const active = isItemActive(item)
                      const showUnread = item.to === '/messages' && unreadMessageCount > 0
                      const glyph = item.image ? (
                        <img src={item.image} alt="" className={styles.iconSize} />
                      ) : (
                        <item.icon className={styles.iconSize} />
                      )
                      // Locked against the destination the icon actually navigates
                      // to, not its declared `to` — see railItemTo.
                      const to = railItemTo(item)
                      if (isNavTargetLocked(to, onboardingLock)) {
                        return (
                          <LockedNavTarget
                            key={item.to}
                            className={`${styles.iconButton} ${styles.locked}`}
                            reason={ONBOARDING_LOCK_MESSAGE}
                            testId="rail-locked-item"
                            label={item.label}
                            // The tour runs while these are locked, so this — not
                            // the Link below — is the branch it points at.
                            ref={register(item.tourId)}
                          >
                            {glyph}
                          </LockedNavTarget>
                        )
                      }
                      return (
                        <Tooltip key={item.to}>
                          <TooltipTrigger asChild>
                            <Link
                              to={to}
                              ref={register(item.tourId)}
                              className={iconClass(item)}
                              aria-label={item.label}
                              aria-current={active ? 'page' : undefined}
                              onClick={(e) => handleRailClick(e, item)}
                            >
                              {active && (
                                <motion.div
                                  className={styles.activeIndicator}
                                  layoutId="rail-indicator"
                                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                />
                              )}
                              {glyph}
                              {showUnread && (
                                <CountBadge
                                  count={unreadMessageCount}
                                  className={styles.unreadBadge}
                                  data-testid="messages-unread-badge"
                                  aria-label={`${unreadMessageCount} unread messages`}
                                />
                              )}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right">{item.label}</TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </nav>
                </div>

                <div className={styles.bottomSection}>
                  {avatarLocked ? (
                    <LockedNavTarget
                      className={`${styles.avatar} ${styles.locked}`}
                      reason={ONBOARDING_LOCK_MESSAGE}
                      testId="sidebar-user-avatar"
                    >
                      {avatarInner}
                    </LockedNavTarget>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to="/settings"
                          className={styles.avatar}
                          data-testid="sidebar-user-avatar"
                          onClick={() => setMobileOpen(false)}
                        >
                          {avatarInner}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="flex flex-col gap-0.5">
                        {profile?.name && <span className="font-medium">{profile.name}</span>}
                        {profile?.email && (
                          <span className="text-xs text-muted-foreground">{profile.email}</span>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Never locked. Signing out is the one thing a founder must be
                    able to do from anywhere, onboarding or not. */}
                  <button onClick={() => logout()} className={styles.logoutButton} title="Logout">
                    <LogOut className={styles.logoutIcon} />
                  </button>
                </div>
              </div>
            </aside>

            <div className={styles.content}>
              <Outlet />
            </div>
          </div>
        </div>
      </OnboardingShellProvider>
    </MobileSidebarContext.Provider>
  )
}
